const IST = "Asia/Kolkata";

const pad = (n: number) => String(n).padStart(2, "0");

type IstParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
};

function istParts(now = new Date()): IstParts {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value || 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
  };
}

export function toIsoDate(year: number, month: number, day: number) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function addDaysIso(iso: string, days: number) {
  const [year, month, day] = iso.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

/** Operational day: 12:00 IST → next 12:00 IST, labeled by the start calendar date. */
export function currentOperationalDate(now = new Date()) {
  const p = istParts(now);
  const calendar = toIsoDate(p.year, p.month, p.day);
  return p.hour < 12 ? addDaysIso(calendar, -1) : calendar;
}

export function mondayOfIso(iso: string) {
  const [year, month, day] = iso.split("-").map(Number);
  const jsDay = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return addDaysIso(iso, -(jsDay === 0 ? 6 : jsDay - 1));
}

export function monthStartOfIso(iso: string) {
  return `${iso.slice(0, 7)}-01`;
}

export function monthBounds(year: number, month: number) {
  const startDate = toIsoDate(year, month, 1);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { startDate, endDate: toIsoDate(year, month, last) };
}

/** Monday–Sunday weeks clipped to the month. */
export function weeksInMonth(year: number, month: number) {
  const { startDate, endDate } = monthBounds(year, month);
  const weeks: { startDate: string; endDate: string }[] = [];
  let cursor = startDate;
  while (cursor <= endDate) {
    const monday = mondayOfIso(cursor);
    const sunday = addDaysIso(monday, 6);
    weeks.push({
      startDate: monday < startDate ? startDate : monday,
      endDate: sunday > endDate ? endDate : sunday,
    });
    cursor = addDaysIso(sunday, 1);
  }
  return weeks;
}

export function weekContaining(iso: string, year: number, month: number) {
  const weeks = weeksInMonth(year, month);
  return (
    weeks.find((week) => iso >= week.startDate && iso <= week.endDate) ??
    weeks[0] ??
    monthBounds(year, month)
  );
}

export function sameDayInMonth(iso: string, year: number, month: number) {
  const wanted = Number(iso.slice(8, 10)) || 1;
  const last = Number(monthBounds(year, month).endDate.slice(8, 10));
  return toIsoDate(year, month, Math.min(Math.max(wanted, 1), last));
}

export function operationalRangeForPreset(preset: "day" | "week" | "month") {
  const today = currentOperationalDate();
  if (preset === "day") return { startDate: today, endDate: today };
  if (preset === "week") {
    return { startDate: mondayOfIso(today), endDate: today };
  }
  return { startDate: monthStartOfIso(today), endDate: today };
}

export function yearOperationalRange(year: number) {
  const today = currentOperationalDate();
  const startDate = `${year}-01-01`;
  const endDate = year === Number(today.slice(0, 4)) ? today : `${year}-12-31`;
  return { year, startDate, endDate };
}

export function inferOperationalPreset(
  startDate: string,
  endDate: string,
): "day" | "week" | "month" | "custom" {
  if (startDate === endDate) return "day";
  if (startDate === mondayOfIso(endDate)) return "week";
  if (
    startDate === monthStartOfIso(endDate) &&
    startDate.slice(0, 7) === endDate.slice(0, 7)
  ) {
    return "month";
  }
  return "custom";
}

export function weekdayLabel(iso: string) {
  const [year, month, day] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(year, month - 1, day));
  return dt.toLocaleDateString("en-GB", {
    weekday: "short",
    timeZone: "UTC",
  });
}

export function periodCaption(
  preset: "day" | "week" | "month" | "custom",
  startDate: string,
  endDate: string,
) {
  if (preset === "day") {
    return `Operational day ${startDate} (12:00 IST → next noon)`;
  }
  if (preset === "week") {
    return `Week ${startDate} → ${endDate} (noon IST days)`;
  }
  if (preset === "month") {
    return `Month ${startDate} → ${endDate} (noon IST days)`;
  }
  return `Custom ${startDate} → ${endDate} (noon IST days)`;
}
